/**
 * QAPage
 *
 * Admin-only page at /testing. Houses testing tooling, organized by tab.
 *
 * Routes:
 *   /testing            → Data Checks (default): smoke tests + claims data checks
 *   /testing/locations  → Locations: read-only coordinate-anomaly finder
 *   /testing/labels     → Labels: entities in multiple labels of one label type
 *   /testing/trends     → Trends: historical data availability per entity
 *   /testing/providers  → Providers: most recently added providers
 */

import React from 'react';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { withRouter } from 'utils';
import { SmokeTestCard } from 'qa/SmokeTestCard';
import { ServicelineCheckCard } from 'qa/ServicelineCheckCard';
import { ReimbursementCheckCard } from 'qa/ReimbursementCheckCard';
import { LocationCoordinateCheckCard } from 'qa/LocationCoordinateCheckCard';
import { LabelConflictsCard } from 'qa/LabelConflictsCard';
import { TrendsAvailabilityCard } from 'qa/TrendsAvailabilityCard';
import { RecentProvidersCard } from 'qa/RecentProvidersCard';
import { QATabs, QA_TABS } from 'qa/QATabs';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
});

const DEFAULT_TAB = 'checks';

function QAPage({ classes, params }) {
  // An unrecognized :tab segment falls back to the default rather than rendering
  // an empty page with no tab selected.
  const requested = params?.tab;
  const tab = QA_TABS.some((t) => t.value === requested) ? requested : DEFAULT_TAB;

  return (
    <>
      <QATabs tab={tab} />
      <div className={classes.container}>
        {tab === 'locations' && <LocationCoordinateCheckCard />}
        {tab === 'labels' && (
          <>
            <LabelConflictsCard entityType='provider' />
            <LabelConflictsCard entityType='organization' />
          </>
        )}
        {tab === 'checks' && (
          <>
            <SmokeTestCard />
            <ServicelineCheckCard />
            <ReimbursementCheckCard />
          </>
        )}
        {tab === 'trends' && <TrendsAvailabilityCard />}
        {tab === 'providers' && <RecentProvidersCard />}
      </div>
    </>
  );
}

QAPage.propTypes = {
  classes: PropTypes.object.isRequired,
  params: PropTypes.object,
};

const StyledQAPage = withStyles(styles)(QAPage);
const RoutedQAPage = withRouter(StyledQAPage);
export { RoutedQAPage as QAPage };
